import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { ClipboardList, FileText, LayoutDashboard, LogOut, Map, Users, Wallet, Star, Coins, CalendarDays, MoreHorizontal, Landmark } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

const NAV = {
  admin: [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, id: "dashboard" },
    { to: "/admin/reports", label: "Report Trip", icon: FileText, id: "reports" },
    { to: "/admin/schedule", label: "Jadwal", icon: CalendarDays, id: "schedule" },
    { to: "/admin/payments", label: "Pembayaran", icon: Wallet, id: "payments" },
    { to: "/admin/vendor-income", label: "Pendapatan Vendor", icon: Landmark, id: "vendor-income" },
    { to: "/admin/susuk", label: "Rekap Susuk", icon: Coins, id: "susuk" },
    { to: "/admin/reviews", label: "Review", icon: Star, id: "reviews" },
    { to: "/admin/tours", label: "Produk Tour", icon: Map, id: "tours" },
    { to: "/admin/team", label: "Team Driver", icon: Users, id: "team" },
  ],
  team: [
    { to: "/driver", label: "Report Saya", icon: ClipboardList, end: true, id: "my-reports" },
    { to: "/driver/schedule", label: "Jadwal", icon: CalendarDays, id: "my-schedule" },
    { to: "/driver/payments", label: "Pembayaran", icon: Wallet, id: "my-payments" },
    { to: "/driver/susuk", label: "Susuk", icon: Coins, id: "my-susuk" },
    { to: "/driver/reviews", label: "Review", icon: Star, id: "my-reviews", badge: true },
  ],
};

const linkCls = ({ isActive }) =>
  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
    isActive ? "bg-emerald-600 text-white shadow-sm" : "text-slate-300 hover:bg-white/5 hover:text-white"
  }`;

const mobileCls = ({ isActive }) =>
  `relative flex flex-1 shrink-0 min-w-[60px] flex-col items-center justify-center gap-1 px-1 py-2 min-h-[56px] text-[10px] font-semibold whitespace-nowrap transition-colors ${
    isActive ? "text-emerald-600" : "text-slate-500"
  }`;

function useUnseenReviews(role) {
  const [count, setCount] = useState(0);
  const location = useLocation();
  useEffect(() => {
    if (role !== "team") return;
    api.get("/reviews/unseen").then((r) => setCount(r.data.count)).catch(() => {});
  }, [role, location.pathname]);
  useEffect(() => {
    const reset = () => setCount(0);
    window.addEventListener("reviews-seen", reset);
    return () => window.removeEventListener("reviews-seen", reset);
  }, []);
  return count;
}

function NewBadge({ count, mobile, id }) {
  if (!count) return null;
  return (
    <span
      data-testid={`${mobile ? "mobile-" : ""}nav-badge-${id}`}
      className={`rounded-full bg-pink-500 text-white font-bold leading-none ${mobile ? "absolute top-1 right-1/2 translate-x-5 px-1.5 py-0.5 text-[9px]" : "ml-auto px-2 py-1 text-[10px]"}`}
    >
      {mobile ? count : `${count} baru`}
    </span>
  );
}

function MobileNav({ items, unseen }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const main = items.length > 5 ? items.slice(0, 4) : items;
  const more = items.length > 5 ? items.slice(4) : [];
  const moreActive = more.some((it) => location.pathname.startsWith(it.to));
  return (
    <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur-xl border-t flex pb-[env(safe-area-inset-bottom)]" data-testid="mobile-nav">
      {main.map((it) => (
        <NavLink key={it.to} to={it.to} end={it.end} className={mobileCls} data-testid={`mobile-nav-${it.id}`}>
          <it.icon className="h-5 w-5" /> {it.label}
          {it.badge && <NewBadge count={unseen} id={it.id} mobile />}
        </NavLink>
      ))}
      {more.length > 0 && (
        <button onClick={() => setOpen(true)} className={mobileCls({ isActive: moreActive })} data-testid="mobile-nav-more">
          <MoreHorizontal className="h-5 w-5" /> Lainnya
        </button>
      )}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="rounded-t-3xl pb-8" data-testid="mobile-more-sheet">
          <SheetHeader className="text-left mb-3"><SheetTitle className="font-heading">Menu Lainnya</SheetTitle><SheetDescription>Akses cepat halaman lain</SheetDescription></SheetHeader>
          <div className="grid grid-cols-2 gap-2">
            {more.map((it) => (
              <NavLink key={it.to} to={it.to} onClick={() => setOpen(false)} className={({ isActive }) => `flex items-center gap-3 min-h-[52px] rounded-xl px-4 text-sm font-semibold ${isActive ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-800"}`} data-testid={`mobile-nav-${it.id}`}>
                <it.icon className="h-5 w-5" /> {it.label}
              </NavLink>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </nav>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-2.5">
      <img src="/logo.png" alt="Dewata Memories Tours" className="h-10 w-10 rounded-lg bg-white object-contain" data-testid="brand-logo" />
      <div className="leading-tight">
        <p className="font-heading font-bold text-base">Dewata Memories</p>
        <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-400">Tours · Driver Report</p>
      </div>
    </div>
  );
}

export function AppShell({ role }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const items = NAV[role];
  const unseen = useUnseenReviews(role);
  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen lg:pl-64">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col bg-slate-950 text-white p-5 gap-8" data-testid="sidebar">
        <Brand />
        <nav className="flex flex-col gap-1">
          {items.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.end} className={linkCls} data-testid={`nav-${it.id}`}>
              <it.icon className="h-4 w-4" /> {it.label}
              {it.badge && <NewBadge count={unseen} id={it.id} />}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto rounded-xl bg-white/5 p-4">
          <p className="text-sm font-semibold truncate" data-testid="current-user-name">{user.name}</p>
          <p className="text-xs text-slate-400 truncate">{user.email}</p>
          <button onClick={doLogout} className="mt-3 flex items-center gap-2 text-xs text-slate-300 hover:text-white" data-testid="logout-btn">
            <LogOut className="h-3.5 w-3.5" /> Keluar
          </button>
        </div>
      </aside>

      <header className="lg:hidden sticky top-0 z-30 bg-slate-950 text-white px-4 py-3 flex items-center justify-between">
        <Brand />
        <button onClick={doLogout} className="h-11 w-11 grid place-items-center rounded-lg hover:bg-white/10" data-testid="mobile-logout-btn" aria-label="Keluar">
          <LogOut className="h-4 w-4" />
        </button>
      </header>

      <main className="px-4 sm:px-6 lg:px-10 py-6 lg:py-10 pb-28 lg:pb-10 max-w-7xl">
        <Outlet />
      </main>

      <MobileNav items={items} unseen={unseen} />
    </div>
  );
}
